import { Injectable } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpParams,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { FormConfig } from './model/ad-admin';
import { MlTextRecord } from 'src/app/Theme/shared/service/ml-description.service';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

/** Combined homepage content for one layout (GET /AD/homepage/content/). */
export interface HomepageContentResponse {
  layout_display_id: string;
  carousel?: unknown[];
  carouselText?: { id?: number; title?: MlTextRecord; text?: MlTextRecord } | null;
  popular?: unknown[];
  bestsellers?: unknown[];
  topics?: unknown[];
}

/** Options for layout-scoped homepage product GETs. */
export interface HomepageProductsGetOptions {
  /**
   * Guest/customer view: no session cookies (bypasses Jwt/Error interceptors)
   * and sends `as_anonymous=true` so the API applies public catalog rules.
   */
  asAnonymous?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AdAdminService {
  private configData: any
  private host = environment.apiUrlHost;
  /**
   * Cross-origin client without interceptors — used for guest preview GETs so
   * admin cookies are not sent (JwtInterceptor always forces withCredentials).
   */
  private readonly anonymousHttp: HttpClient;

  constructor(private http: HttpClient, httpBackend: HttpBackend) {
    this.anonymousHttp = new HttpClient(httpBackend);
  }

  private layoutParams(layoutDisplayId?: string | null): HttpParams | undefined {
    if (!layoutDisplayId) {
      return undefined;
    }
    return new HttpParams().set('layout_display_id', layoutDisplayId);
  }

  private layoutOptions(layoutDisplayId?: string | null): { params?: HttpParams } {
    const params = this.layoutParams(layoutDisplayId);
    return params ? { params } : {};
  }

  /** Query params for nested product list GETs (optional guest flag). */
  private homepageProductsParams(
    layoutDisplayId?: string | null,
    options?: HomepageProductsGetOptions,
  ): HttpParams {
    let params = new HttpParams();
    if (layoutDisplayId) {
      params = params.set('layout_display_id', layoutDisplayId);
    }
    if (options?.asAnonymous) {
      params = params.set('as_anonymous', 'true');
    }
    return params;
  }

  private mergeLayoutIntoBody(data: unknown, layoutDisplayId?: string | null): Record<string, unknown> {
    const body =
      data && typeof data === 'object' && !Array.isArray(data)
        ? { ...(data as Record<string, unknown>) }
        : {};
    if (layoutDisplayId) {
      body['layout_display_id'] = layoutDisplayId;
    }
    return body;
  }

  getFormConfig(): Observable<FormConfig> {
    // Replace with your API endpoint
    return this.http.get<FormConfig>('/api/form-config');
  }

  getSections(model: any, interfaceName: any): Observable<any> {
    const query1 = new HttpParams()
      .set('model_name', model)
      .set('interface', interfaceName);

    return this.http.get<any>(`${this.host}/AD/sectionGroupTree/`, { params: query1 });
  }

  getGroups(model: any, interfaceName: any): Observable<any> {
    const query1 = new HttpParams()
      .set('model_name', model)
      .set('interface', interfaceName);

    return this.http.get<any>(`${this.host}/AD/group/`, { params: query1 });
  }
  retreiveModelFields(model) {
    const query1 = new HttpParams().set('model_name', model)
    return this.http.get<any>(`${this.host}/RetrieveModelFields`, { params: query1 });
  }

  retreiveModelFieldswWithData(model) {
    const query1 = new HttpParams().set('model_name', model)
    return this.http.get<any>(`${this.host}/RetrieveModelFieldsWithData`, { params: query1 });
  }
  getDataS3File(): Observable<any | HttpErrorResponse> {
    const configUrl = `${this.host}/CP/save-theme-config/`;
    return this.http.get<any>(configUrl).pipe(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      tap(data => this.configData = data), // Cache the fetched config data
      catchError(this.handleError<any>('getConfig', []))
    );
  }

  // Error handling
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // Let the app keep running by returning an empty result.
      return of(result);
    };
  }
  public saveSection(sectionData): Observable<any> {
    return this.http.post(`${this.host}/CP/Cp_flexibleLayout/`, sectionData)
  }

  /** GET /AD/homepage/content/ — all sections for one layout or country. */
  public getHomepageContent(
    layoutDisplayId?: string | null,
    countryCode?: string | null,
  ): Observable<HomepageContentResponse> {
    let params = new HttpParams();
    if (layoutDisplayId) {
      params = params.set('layout_display_id', layoutDisplayId);
    }
    if (countryCode) {
      params = params.set('country_code', countryCode);
    }
    return this.http.get<HomepageContentResponse>(`${this.host}/AD/homepage/content/`, { params });
  }

  public getCarouselImages(layoutDisplayId?: string | null): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/carousel/`, this.layoutOptions(layoutDisplayId));
  }

  /** POST upsert carousel text for a layout (201 create / 200 update). */
  public addCarouselText(data: any, layoutDisplayId?: string | null): Observable<any> {
    return this.http.post(`${this.host}/AD/carouselText/`, data, this.layoutOptions(layoutDisplayId));
  }

  /**
   * Add a carousel image. BE accepts either:
   * - cf_image: id of an existing image from image management (JSON body)
   * - image: file upload (multipart/form-data)
   */
  public addCarouselImage(
    payload: { cf_image?: number; image?: File; default?: boolean; slot?: number },
    layoutDisplayId?: string | null,
  ): Observable<any> {
    const options = this.layoutOptions(layoutDisplayId);
    if (payload.image instanceof File) {
      const file = payload.image;
      const formData = new FormData();
      formData.append('image', file, file.name);
      formData.append('default', String(payload.default ?? false));
      if (payload.slot != null) formData.append('slot', String(payload.slot));
      if (layoutDisplayId) {
        formData.append('layout_display_id', layoutDisplayId);
      }
      console.log('[Carousel upload] POST /AD/carousel/ (multipart)', {
        layout_display_id: layoutDisplayId ?? null,
        default: payload.default ?? false,
        slot: payload.slot ?? null,
        image: {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
        },
        file,
      });
      return this.http.post(`${this.host}/AD/carousel/`, formData, options);
    }
    const body: Record<string, unknown> = {
      cf_image: payload.cf_image,
      default: payload.default ?? false,
    };
    if (payload.slot != null) body['slot'] = payload.slot;
    if (layoutDisplayId) {
      body['layout_display_id'] = layoutDisplayId;
    }
    console.log('[Carousel upload] POST /AD/carousel/ (json)', {
      url: `${this.host}/AD/carousel/`,
      layout_display_id: layoutDisplayId ?? null,
      body,
    });
    return this.http.post(`${this.host}/AD/carousel/`, body, options);
  }

  public deleteCarouselImage(id: number | string, layoutDisplayId?: string | null): Observable<any> {
    return this.http.delete(`${this.host}/AD/carousel/${id}/`, this.layoutOptions(layoutDisplayId));
  }

  /** Update existing carousel slot (cf_image and/or default flag). */
  public updateCarouselImage(
    carouselId: number,
    payload: { cf_image?: number; default?: boolean },
    layoutDisplayId?: string | null,
  ): Observable<any> {
    return this.http.patch(`${this.host}/AD/carousel/${carouselId}/`, payload, this.layoutOptions(layoutDisplayId));
  }

  public getCarouselText(layoutDisplayId?: string | null): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/carouselText/`, this.layoutOptions(layoutDisplayId));
  }

  public getTopics(layoutDisplayId?: string | null): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/topics/`, this.layoutOptions(layoutDisplayId));
  }

  public addTopic(data: unknown, layoutDisplayId?: string | null): Observable<any> {
    const body = this.mergeLayoutIntoBody(data, layoutDisplayId);
    return this.http.post(`${this.host}/AD/topics/`, body, this.layoutOptions(layoutDisplayId));
  }

  public updateTopic(id: number | string, data: unknown, layoutDisplayId?: string | null): Observable<any | HttpErrorResponse> {
    const body = this.mergeLayoutIntoBody(data, layoutDisplayId);
    return this.http.put<any>(`${this.host}/AD/topics/${id}/`, body, this.layoutOptions(layoutDisplayId));
  }

  public deleteTopic(id: number | string, layoutDisplayId?: string | null): Observable<any> {
    return this.http.delete(`${this.host}/AD/topics/${id}/`, this.layoutOptions(layoutDisplayId));
  }

  public getQuestions(layoutDisplayId?: string | null): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/questions/`, this.layoutOptions(layoutDisplayId));
  }

  public addQuestion(data: unknown, layoutDisplayId?: string | null): Observable<any> {
    const body = this.mergeLayoutIntoBody(data, layoutDisplayId);
    return this.http.post(`${this.host}/AD/questions/`, body, this.layoutOptions(layoutDisplayId));
  }

  public updateQuestion(id: number | string, data: unknown, layoutDisplayId?: string | null): Observable<any | HttpErrorResponse> {
    const body = this.mergeLayoutIntoBody(data, layoutDisplayId);
    return this.http.put<any>(`${this.host}/AD/questions/${id}/`, body, this.layoutOptions(layoutDisplayId));
  }

  public deleteQuestion(id: number | string, layoutDisplayId?: string | null): Observable<any> {
    return this.http.delete<any>(`${this.host}/AD/questions/${id}/`, this.layoutOptions(layoutDisplayId));
  }

    public getInvoiceListFields(): Observable<any[]> {
      return this.http.get<any[]>(`${this.host}/AD/InvoiceFiledsSave/`);
    }
    public getShipmentListFields(): Observable<any[]> {
      return this.http.get<any[]>(`${this.host}/AD/ShipmentFiledsSave/`);
    }

  public getOrderListFields(): Observable<any[]> {
    return this.http.get<any[]>(`${this.host}/AD/OrderFiledsSave/`);
  }

  public getPopularProducts(layoutDisplayId?: string | null): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/popular/`, this.layoutOptions(layoutDisplayId));
  }

  /** Popular rows with nested CF_Item details for storefront / admin preview. */
  public getPopularProductsWithDetails(
    layoutDisplayId?: string | null,
    options?: HomepageProductsGetOptions,
  ): Observable<any> {
    const params = this.homepageProductsParams(layoutDisplayId, options);
    const url = `${this.host}/AD/popularProducts/`;
    if (options?.asAnonymous) {
      return this.anonymousHttp.get<any>(url, { params, withCredentials: false });
    }
    return this.http.get<any>(url, { params });
  }

  public addPopularProducts(data: unknown, layoutDisplayId?: string | null): Observable<any> {
    const body = this.mergeLayoutIntoBody(data, layoutDisplayId);
    return this.http.post(`${this.host}/AD/popular/`, body, this.layoutOptions(layoutDisplayId));
  }

  /** PUT by CF_Item product id — updates displayHome and/or sequence. */
  public updatePopularProducts(
    productId: number | string,
    data: unknown,
    layoutDisplayId?: string | null,
  ): Observable<any> {
    const body = this.mergeLayoutIntoBody(data, layoutDisplayId);
    return this.http.put(`${this.host}/AD/popular/${productId}/`, body, this.layoutOptions(layoutDisplayId));
  }

  public deletePopularProduct(rowId: number | string, layoutDisplayId?: string | null): Observable<any> {
    return this.http.delete(`${this.host}/AD/popular/${rowId}/`, this.layoutOptions(layoutDisplayId));
  }

  public getBestSellerProducts(layoutDisplayId?: string | null): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/bestsellers/`, this.layoutOptions(layoutDisplayId));
  }

  public getBestSellerProductsWithDetails(
    layoutDisplayId?: string | null,
    options?: HomepageProductsGetOptions,
  ): Observable<any> {
    const params = this.homepageProductsParams(layoutDisplayId, options);
    const url = `${this.host}/AD/bestsellersProducts/`;
    if (options?.asAnonymous) {
      return this.anonymousHttp.get<any>(url, { params, withCredentials: false });
    }
    return this.http.get<any>(url, { params });
  }

  public addBestSellerProducts(data: unknown, layoutDisplayId?: string | null): Observable<any> {
    const body = this.mergeLayoutIntoBody(data, layoutDisplayId);
    return this.http.post(`${this.host}/AD/bestsellers/`, body, this.layoutOptions(layoutDisplayId));
  }

  public updateBestSellerProducts(
    productId: number | string,
    data: unknown,
    layoutDisplayId?: string | null,
  ): Observable<any> {
    const body = this.mergeLayoutIntoBody(data, layoutDisplayId);
    return this.http.put(`${this.host}/AD/bestsellers/${productId}/`, body, this.layoutOptions(layoutDisplayId));
  }

  public deleteBestSellerProduct(rowId: number | string, layoutDisplayId?: string | null): Observable<any> {
    return this.http.delete(`${this.host}/AD/bestsellers/${rowId}/`, this.layoutOptions(layoutDisplayId));
  }
  // add new extension field
  public extensionField(model_name: string, fieldsToAdd: any[]): Observable<any | HttpErrorResponse> {
    const requestBody = { fields_to_add: fieldsToAdd };
    console.log('request_body', requestBody);
    return this.http.post<any>(`${this.host}/AddExtensionFields?model_name=${model_name}`, requestBody, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json' // Set the Content-Type header
      })
    });
  }

  public getProductFields(): Observable<any> {
    return this.http.get(`${this.host}/AD/CPItemDetailsForFlexible/`);
  }

  public getThemeConfig(): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/themes/`)
  }
  public getButtonConfigs(): Observable<any> {
    return this.http.get<any>(`${this.host}/AD/button-configs/`)
  }
  public sendColors(data:any): Observable<any> {
    return this.http.post<any>(`${this.host}/AD/color-palettes/`,data);
  }
  public saveFooterAboutUs(data): Observable<any> {
    // If data is FormData, send it directly (browser will set Content-Type automatically)
    if (data instanceof FormData) {
      return this.http.post(`${this.host}/AD/aboutus/`, data);
    } else {
      // If no file, send as JSON
      return this.http.post(`${this.host}/AD/aboutus/`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }
  public getFooterAboutUs(sectionData): Observable<any> {
    return this.http.get(`${this.host}/AD/aboutus/`, sectionData)
  }
  public updateFooterAboutUs(id: number, data: any): Observable<any> {
    // If data is FormData, send it directly (browser will set Content-Type automatically)
    if (data instanceof FormData) {
      return this.http.put(`${this.host}/AD/aboutus/${id}/`, data);
    } else {
      // If no file, send as JSON
      return this.http.put(`${this.host}/AD/aboutus/${id}/`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  // Support Logos methods
  public saveSupportLogos(data: any): Observable<any> {
    console.log('Service - saveSupportLogos called with data:', data);

    // If data is already FormData, send it directly
    if (data instanceof FormData) {
      console.log('Service - Sending FormData directly');
      return this.http.post(`${this.host}/AD/supportlogos/`, data);
    }

    // If data contains a file, use FormData
    if (data.logo && data.logo instanceof File) {
      const formData = new FormData();
      formData.append('logo', data.logo);
      formData.append('logo_url', data.logo_url || '');
      formData.append('logo_link', data.logo_link || '');

      console.log('Service - Sending FormData with file:', {
        hasFile: true,
        fileName: data.logo.name,
        fileSize: data.logo.size,
        logo_url: data.logo_url,
        logo_link: data.logo_link
      });

      return this.http.post(`${this.host}/AD/supportlogos/`, formData);
    } else {
      // If no file, send as JSON
      console.log('Service - Sending JSON data:', data);
      return this.http.post(`${this.host}/AD/supportlogos/`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  // Bulk save for support logos (expects an array of entries or FormData)
  public saveSupportLogosBulk(data: Array<{ logo_url: string; logo_link: string }> | FormData): Observable<any> {
    console.log('Service - saveSupportLogosBulk called with data:', data);

    // Check if it's FormData
    if (data instanceof FormData) {
      return this.http.post(`${this.host}/AD/supportlogos/`, data);
    } else {
      // Send as JSON
      return this.http.post(`${this.host}/AD/supportlogos/`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  public getSupportLogos(): Observable<any> {
    return this.http.get(`${this.host}/AD/supportlogos/`);
  }

  public updateSupportLogos(id: number, data: any): Observable<any> {
    return this.http.put(`${this.host}/supportlogos/${id}/`, data);
  }


  public deleteSupportLogos(id: number): Observable<any> {
    return this.http.delete(`${this.host}/AD/supportlogos/${id}/`);
  }

  public uploadSupportLogo(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('logo', file);
    return this.http.post(`${this.host}/AD/upload-support-logo/`, formData);
  }

  // Terms and Conditions methods
  public saveTermsAndConditions(data: any): Observable<any> {
    console.log('Service - saveTermsAndConditions called with data:', data);

    // If data is already FormData, send it directly
    if (data instanceof FormData) {
      console.log('Service - Sending FormData directly');
      return this.http.post(`${this.host}/AD/termsandconditions/`, data);
    }

    // If data contains a file, use FormData
    if (data.document && data.document instanceof File) {
      const formData = new FormData();
      const title = data.title || 'Untitled Document';
      formData.append('title', title);
      formData.append('content', data.content || '');
      formData.append('document', data.document);
      formData.append('document_type', data.document_type || 'uploaded');

      console.log('Service - Sending FormData with file:', {
        title: data.title,
        content: data.content,
        hasFile: true,
        fileName: data.document.name,
        fileSize: data.document.size,
        documentType: data.document_type
      });
      console.log('title', title);


      return this.http.post(`${this.host}/AD/termsandconditions/`, formData);
    } else {
      // If no file, send as JSON
      console.log('Service - Sending JSON data:', data);
      return this.http.post(`${this.host}/AD/termsandconditions/`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  public getTermsAndConditions(): Observable<any> {
    return this.http.get(`${this.host}/AD/termsandconditions/`);
  }

  public updateTermsAndConditions(id: number, data: any): Observable<any> {
    // If data is FormData, send it directly (browser will set Content-Type automatically)
    if (data instanceof FormData) {
      console.log('Service - Updating with FormData');
      return this.http.put(`${this.host}/AD/termsandconditions/${id}/`, data);
    } else {
      // If no file, send as JSON
      console.log('Service - Updating with JSON data');
      return this.http.put(`${this.host}/AD/termsandconditions/${id}/`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  public deleteTermsAndConditions(id: number): Observable<any> {
    return this.http.delete(`${this.host}/AD/termsandconditions/${id}/`);
  }

  // Social Media Links methods
  public saveSocialMediaLink(data: any): Observable<any> {
    return this.http.post(`${this.host}/AD/socialmedia/`, data);
  }

  public getSocialMediaLinks(): Observable<any> {
    return this.http.get(`${this.host}/AD/socialmedia/`);
  }

  // Bulk save for social media links
  public saveSocialMediaLinksBulk(data: Array<{ name: string; link: string }>): Observable<any> {
    // Send plain array of { name, link }
    return this.http.post(`${this.host}/AD/socialmedia/`, data);
  }

  // Privacy Policy methods
  public savePrivacyPolicy(formData: FormData | any): Observable<any> {
    // If data is FormData, send it directly (browser will set Content-Type automatically)
    if (formData instanceof FormData) {
      return this.http.post(`${this.host}/AD/privacypolicy/`, formData);
    } else {
      // If no file, send as JSON
      return this.http.post(`${this.host}/AD/privacypolicy/`, formData, {
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  public getPrivacyPolicy(): Observable<any> {
    return this.http.get(`${this.host}/AD/privacypolicy/`);
  }

  public updatePrivacyPolicy(id: number, formData: FormData | any): Observable<any> {
    // If data is FormData, send it directly (browser will set Content-Type automatically)
    if (formData instanceof FormData) {
      return this.http.put(`${this.host}/AD/privacypolicy/${id}/`, formData);
    } else {
      // If no file, send as JSON
      return this.http.put(`${this.host}/AD/privacypolicy/${id}/`, formData, {
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  public deletePrivacyPolicy(id: number): Observable<any> {
    return this.http.delete(`${this.host}/AD/privacypolicy/${id}/`);
  }
}
